import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  Input,
  DrawerFooter,
  Text,
  Box,
  Radio,
  RadioGroup,
  Stack,
  VStack,
  Divider,
} from "@chakra-ui/react";
import React, { useState } from "react";
import useFiltersStore from "../store/useFiltersStore";
import { useOrdersStore } from "../store/useOrdersStore";
import usePreferenceStore from "../../../components/usePreferencesStore";
import { ORDER_VIEW_OPTIONS, OrderViewPreference } from "../../../constants/orderView";
import { MdKeyboardReturn } from "react-icons/md";

type SideSettingsTabProps = {
  isOpen: boolean;
  onClose: () => void;
  setOrderStartIndex: React.Dispatch<React.SetStateAction<number>>;
  setIsFiltersChanged: React.Dispatch<React.SetStateAction<boolean>>;
  refetchAllOrders: () => void;
};

const SideSettingsTab: React.FC<SideSettingsTabProps> = ({
  isOpen,
  onClose,
  setOrderStartIndex,
  setIsFiltersChanged,
  refetchAllOrders,
}) => {
  const filterObject = useFiltersStore((state) => state.filterObject);
  const setFilterObject = useFiltersStore((state) => state.setFilterObject);
  const [ordersPerPageError, setOrdersPerPageError] = useState("");
  const setQueryOffset = useOrdersStore((state) => state.setQueryOffset);

  const [localFilterObject, setLocalFilterObject] = useState<{ limit: number }>(
    filterObject
  );

  const orderViewPreference = usePreferenceStore((state) => state.orderViewPreference);
  const [localOrderViewPreference, setLocalOrderViewPreference] = useState<OrderViewPreference>(orderViewPreference);
  const setOrderViewPreference = usePreferenceStore((state) => state.setOrderViewPreference);

  function handleSettingsApply() {
    setFilterObject({
      ...filterObject,
      limit: localFilterObject.limit,
    });
    setOrderViewPreference(localOrderViewPreference);
    setQueryOffset(0);
    setOrderStartIndex(1);
    setIsFiltersChanged(true);
    onClose();
  }

  function handleSettingsApplyKeyDown(e: React.KeyboardEvent) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (ordersPerPageError !== "") return;
    handleSettingsApply();
  }

  const handleOrderViewChange = (value: string) => {
    setLocalOrderViewPreference(value as OrderViewPreference);
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      returnFocusOnClose={false}
    >
      <DrawerContent>
        <DrawerHeader>
          <Text
            fontWeight={"bold"}
            borderBottom={"0.5px solid lightgrey"}
            mb={3}
            fontSize={"x-large"}
          >
            Preferences
          </Text>
        </DrawerHeader>

        <DrawerBody onKeyDown={handleSettingsApplyKeyDown}>
          <VStack spacing={6} align="stretch">
            <Box>
              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Text fontSize={"smaller"} alignSelf={"flex-start"}>
                  Orders per Page
                </Text>
                <Box
                  width={"fit-content"}
                  display={"flex"}
                  flexDirection={"column"}
                  alignItems={"flex-end"}
                >
                  <Input
                    type="number"
                    width={"50%"}
                    size={"xs"}
                    value={localFilterObject.limit}
                    onChange={(e) => {
                      if (
                        e.target.value === "" ||
                        parseInt(e.target.value) === 0
                      ) {
                        setOrdersPerPageError("should be more than 0");
                      } else {
                        setOrdersPerPageError("");
                      }

                      setLocalFilterObject({
                        ...localFilterObject,
                        limit: parseInt(e.target.value),
                      });
                    }}
                  ></Input>
                  {ordersPerPageError !== "" && (
                    <Text color={"red"} fontSize={"x-small"}>
                      {ordersPerPageError}
                    </Text>
                  )}
                </Box>
              </Box>
            </Box>

            <Divider />
            <Box>
              <Text fontSize={"smaller"} fontWeight="medium" mb={3}>
                Order View Preference
              </Text>
              <RadioGroup value={localOrderViewPreference} onChange={handleOrderViewChange}>
                <Stack direction="column" spacing={3}>
                  {ORDER_VIEW_OPTIONS.map((opt) => (
                    <Radio key={opt.value} value={opt.value} size="sm">
                      <Box>
                        <Text fontSize="sm" fontWeight="medium">{opt.label}</Text>
                      </Box>
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
            </Box>
          </VStack>
        </DrawerBody>

        <DrawerFooter>
          <Button variant="outline" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={"outline"}
            backgroundColor={"#4895ef"}
            color={"white"}
            isDisabled={ordersPerPageError !== ""}
            onClick={handleSettingsApply}
            rightIcon={<MdKeyboardReturn />}
          >
            Apply
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default SideSettingsTab;
